import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
} from '@mui/material';
import agent from '../api/axios';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

interface CheckoutFormProps {
  clientSecret: string;
  businessId: string;
  subscriptionId: string;
  customerId: string;
  onComplete: () => void;
}

interface PaymentFormProps {
  businessId: string;
  subscriptionId: string;
  customerId: string;
  onComplete: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  businessId,
  subscriptionId,
  customerId,
  onComplete,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm the setup intent
      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/registration-success',
        },
        redirect: 'if_required',
      });

      if (submitError) {
        console.error('Stripe error:', submitError);
        setError(submitError.message || 'An error occurred');
        setIsProcessing(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Call backend to create subscription
        await agent.Business.confirmPayment({
          businessId,
          subscriptionId,
          paymentMethodId: setupIntent.payment_method as string,
        });

        onComplete();
      } else {
        setError('Payment setup was not completed. Please try again.');
        setIsProcessing(false);
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to process payment'
      );
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!stripe || isProcessing}
        sx={{ mt: 3 }}
      >
        {isProcessing ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Processing...
          </>
        ) : (
          'Complete Registration'
        )}
      </Button>
    </form>
  );
};

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  clientSecret,
  businessId,
  subscriptionId,
  customerId,
  onComplete,
}) => {
  const theme = useTheme();

  // Configure appearance to match app theme
  const appearance = {
    theme:
      theme.palette.mode === 'dark' ? ('night' as const) : ('stripe' as const),
    variables: {
      colorPrimary: theme.palette.primary.main,
      colorBackground: theme.palette.background.paper,
      colorText: theme.palette.text.primary,
      colorDanger: theme.palette.error.main,
      fontFamily: theme.typography.fontFamily,
      borderRadius: theme.shape.borderRadius + 'px',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <Box
      sx={{
        mt: 3,
        bgcolor: 'background.paper',
        borderRadius: 2,
        p: 3,
      }}
    >
      <Elements stripe={stripePromise} options={options}>
        <PaymentForm
          businessId={businessId}
          subscriptionId={subscriptionId}
          customerId={customerId}
          onComplete={onComplete}
        />
      </Elements>
    </Box>
  );
};

export default CheckoutForm;
